/**
 * OrgSettings — per-organization admin page.
 *
 * Tabs: General | Members | Invites | Audit
 *
 * Wires the new P5.3 endpoints:
 *   GET/POST /api/orgs/:orgId/invites  (list + create)
 *   POST/DELETE .../invites/:id/resend, /revoke
 *   GET/PATCH/DELETE /api/orgs/:orgId/members/:accountId
 *   GET /api/orgs/:orgId/audit-logs
 *
 * Owner / admin only. Members tab has change role + remove actions.
 * General tab still shows /me context (display name, principal_type, org).
 */

import { useState } from "react";
import { NavLink } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createOrgInvite,
  getAuthMe,
  listOrgAuditLogs,
  listOrgInvites,
  listOrgMembers,
  removeOrgMember,
  resendOrgInvite,
  revokeOrgInvite,
  updateOrgMember,
  createOrgWebhook,
  deleteOrgWebhook,
  listOrgWebhooks,
  listWebhookDeliveries,
  updateOrgWebhook,
  WEBHOOK_EVENT_TYPES,
  type OrgMember,
  type Webhook,
  type WebhookDelivery,
  type WebhookEventType,
} from "../lib/api";
import {
  collidingPrincipalLabels,
  needsServerBadge,
  principalServer,
} from "../lib/principalIdentity";
import { useToast } from "../components/Toast";

type BadgeVariant = NonNullable<BadgeProps["variant"]>;
import {
  Badge,
  Button,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectIcon,
  SelectList,
  SelectContent,
  SelectItem,
  Avatar,
  AvatarImage,
  AvatarFallback,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  Checkbox,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  EmptyState,
  EmptyStateTitle,
  Skeleton,
  Tabs,
  Textarea,
  TabsList,
  TabsTab,
  TabsIndicator,
} from "raft-ui";
import type { BadgeProps } from "raft-ui";

const ORG_SETTINGS_TAB_LABELS: Record<OrgSettingsTab, string> = {
  general: "General",
  members: "Members",
  invites: "Invites",
  audit: "Audit",
  webhooks: "Webhooks",
};

export const ORG_SETTINGS_TABS = [
  "general",
  "members",
  "invites",
  "audit",
  "webhooks",
] as const;

export type OrgSettingsTab = (typeof ORG_SETTINGS_TABS)[number];

export function isOrgSettingsTab(tab: string | undefined): tab is OrgSettingsTab {
  return ORG_SETTINGS_TABS.some((candidate) => candidate === tab);
}

export function OrgSettings({
  orgId,
  tab,
}: {
  orgId: string;
  tab: OrgSettingsTab;
}) {
  const me = useQuery({ queryKey: ["auth-me"], queryFn: () => getAuthMe() });
  const account = me.data?.account;
  const currentOrgId = account?.org_id ?? null;
  const currentRole = account?.org_role ?? null;
  const isOwnerOrAdmin = currentRole === "owner" || currentRole === "admin";
  const isCurrentOrg = currentOrgId === orgId;

  return (
    <div>
      <div className="mb-6">
        <div className="text-sm text-foreground-muted">Organization</div>
        <h1 className="text-2xl font-bold">
          {account
            ? `${account.server_slug ?? account.server_id} · Raft org`
            : "Settings"}
        </h1>
        <div className="text-sm text-foreground-muted font-mono">{orgId}</div>
      </div>

      {me.isLoading && <p className="text-foreground-muted">Loading…</p>}

      {!isCurrentOrg && currentOrgId && (
        <div className="rounded-md bg-warning-soft px-4 py-3 text-warning-strong text-sm mb-4">
          ⚠ The org in the URL ({orgId}) doesn't match your current org (
          {currentOrgId}). Multi-org support deferred to v2.
        </div>
      )}

      {/* Visual-only Tabs: navigation stays with NavLink; the controlled
          `value` is the active tab (already resolved from the route by the
          parent route component) so the sliding indicator tracks it. */}
      <Tabs value={tab} className="mb-4">
        <TabsList>
          {ORG_SETTINGS_TABS.map((t) => (
            <TabsTab
              key={t}
              value={t}
              render={<NavLink to={`/orgs/${orgId}/${t}`} end />}
            >
              {ORG_SETTINGS_TAB_LABELS[t]}
            </TabsTab>
          ))}
          <TabsIndicator />
        </TabsList>
      </Tabs>

      {tab === "general" && (
        <div className="text-sm space-y-2">
          <Row k="External provider" v={account?.provider ?? "?"} />
          <Row k="External ID (Raft server_id)" v={account?.server_id ?? "?"} />
          <Row k="Server slug" v={account?.server_slug ?? "—"} />
          <Row
            k="Your principal type"
            v={
              account?.principal_type === "agent"
                ? "agent (Raft)"
                : account?.principal_type === "human"
                  ? "human (Raft)"
                  : "—"
            }
          />
          <Row k="Your org_id" v={currentOrgId ?? "—"} mono />
          <Row
            k="Your org_role"
            v={currentRole ?? "—"}
            color={
              currentRole === "owner"
                ? "#a855f7"
                : currentRole === "admin"
                  ? "#3b82f6"
                  : undefined
            }
          />
          <Row
            k="Your server_role (from Raft)"
            v={account?.server_role ?? "—"}
          />
          <p className="text-xs text-foreground-muted pt-3 border-t border-line-hairline">
            Future: editable org name + slug, danger zone (archive org),
            sign-up mode (open vs invite-only).
          </p>
        </div>
      )}

      {tab === "members" && (
        <MembersTab
          orgId={orgId}
          currentAccountId={account?.id ?? null}
          currentRole={currentRole}
        />
      )}

      {tab === "invites" && (
        <InvitesTab orgId={orgId} canManage={isOwnerOrAdmin} />
      )}

      {tab === "audit" && (
        <AuditTab orgId={orgId} canView={isOwnerOrAdmin || currentRole === "member"} />
      )}

      {tab === "webhooks" && (
        <WebhooksTab orgId={orgId} canManage={isOwnerOrAdmin} />
      )}
    </div>
  );
}

function MembersTab({
  orgId,
  currentAccountId,
  currentRole,
}: {
  orgId: string;
  currentAccountId: string | null;
  currentRole: string | null;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const isAdmin = currentRole === "admin" || currentRole === "owner";
  const [principalFilter, setPrincipalFilter] = useState<"all" | "human" | "agent">("all");
  const members = useQuery({
    queryKey: ["org-members", orgId],
    queryFn: () => listOrgMembers(orgId),
  });
  const filteredMembers = (members.data?.members ?? []).filter((m) =>
    principalFilter === "all"
      ? true
      : m.principal_type === principalFilter,
  );
  // Computed over the whole filtered collection, never per row: a row cannot see
  // its peers (see lib/principalIdentity.ts).
  const collidingLabels = collidingPrincipalLabels(filteredMembers);

  const update = useMutation({
    mutationFn: ({ accountId, role }: { accountId: string; role: OrgMember["org_role"] }) =>
      updateOrgMember(orgId, accountId, role),
    onSuccess: () => {
      toast.show({ kind: "success", title: "Member role updated" });
      qc.invalidateQueries({ queryKey: ["org-members", orgId] });
    },
    onError: (e) =>
      toast.show({
        kind: "error",
        title: "Update failed",
        description: (e as Error).message,
      }),
  });

  const remove = useMutation({
    mutationFn: (accountId: string) => removeOrgMember(orgId, accountId),
    onSuccess: () => {
      toast.show({ kind: "success", title: "Member removed" });
      qc.invalidateQueries({ queryKey: ["org-members", orgId] });
    },
    onError: (e) =>
      toast.show({
        kind: "error",
        title: "Remove failed",
        description: (e as Error).message,
      }),
  });

  return (
    <section className="border-t border-line-hairline pt-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-foreground-strong">Members</h3>
        <div className="flex items-center gap-2">
          <Select
            items={{
              all: "All types",
              human: "Humans only",
              agent: "Agents only",
            }}
            value={principalFilter}
            onValueChange={(v) =>
              setPrincipalFilter(v as "all" | "human" | "agent")
            }
          >
            <Tooltip>
              <TooltipTrigger
                render={
                  <SelectTrigger className="text-xs py-0.5">
                    <SelectValue />
                    <SelectIcon />
                  </SelectTrigger>
                }
              />
              <TooltipContent>Filter by principal type</TooltipContent>
            </Tooltip>
            <SelectContent>
              <SelectList>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="human">Humans only</SelectItem>
              <SelectItem value="agent">Agents only</SelectItem>
            </SelectList>
            </SelectContent>
          </Select>
          <span className="text-xs text-foreground-muted">
            {filteredMembers.length} member{filteredMembers.length === 1 ? "" : "s"}
            {principalFilter !== "all" && (
              <span className="ml-1">({principalFilter})</span>
            )}
          </span>
        </div>
      </div>
      {!isAdmin && (
        <p className="text-xs text-warning mb-2">
          ⚠ Your current role is "{currentRole ?? "—"}" — you can view
          members but not edit them.
        </p>
      )}
      {members.isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      )}
      {members.error && (
        <p className="text-danger">Failed: {(members.error as Error).message}</p>
      )}
      {members.data && filteredMembers.length === 0 && (
        <EmptyState>
          <EmptyStateTitle>
            {principalFilter === "all"
              ? "No members yet."
              : `No ${principalFilter} members.`}
          </EmptyStateTitle>
        </EmptyState>
      )}
      {members.data && filteredMembers.length > 0 && (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-foreground-muted text-left border-b border-line-hairline">
              <th className="font-normal py-1 pr-2">Principal</th>
              <th className="font-normal py-1 pr-2">Type</th>
              <th className="font-normal py-1 pr-2">Role</th>
              <th className="font-normal py-1 pr-2">Joined</th>
              <th className="font-normal py-1 pr-2">Last login</th>
              {isAdmin && <th className="font-normal py-1">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filteredMembers.map((m) => (
              <tr
                key={m.account_id}
                className="border-b border-line-hairline hover:bg-fill-muted"
              >
                <td className="py-2 pr-2">
                  <div className="font-medium">
                    {m.display_name}
                    {needsServerBadge(m, collidingLabels) && principalServer(m) && (
                      <Badge variant="muted" className="ml-1.5 align-middle">
                        {principalServer(m)}
                      </Badge>
                    )}
                    {m.account_id === currentAccountId && (
                      <span className="ml-1 text-xs text-foreground-muted">(you)</span>
                    )}
                  </div>
                  <div className="text-xs text-foreground-muted">
                    {m.username ? (
                      <span className="font-mono">@{m.username}</span>
                    ) : (
                      <span className="font-mono">
                        {m.provider_subject.slice(0, 16)}…
                      </span>
                    )}
                  </div>
                </td>
                <td className="py-2 pr-2">
                  {m.principal_type === "agent" ? (
                    <Badge variant="accent">agent</Badge>
                  ) : (
                    <span className="text-xs">human</span>
                  )}
                </td>
                <td className="py-2 pr-2">
                  {isAdmin && m.account_id !== currentAccountId ? (
                    <Select
                      items={Object.fromEntries(
                        (["owner", "admin", "member", "viewer"] as const).map(
                          (r) => [r, r],
                        ),
                      )}
                      value={m.org_role}
                      onValueChange={(v) =>
                        update.mutate({
                          accountId: m.account_id,
                          role: v as OrgMember["org_role"],
                        })
                      }
                      disabled={update.isPending}
                    >
                      <SelectTrigger className="text-xs py-0.5">
                        <SelectValue />
                        <SelectIcon />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectList>
                        {(["owner", "admin", "member", "viewer"] as const).map(
                          (r) => (
                            <SelectItem key={r} value={r}>
                              {r}
                            </SelectItem>
                          ),
                        )}
                      </SelectList>
                      </SelectContent>
                    </Select>
                  ) : (
                    <span
                      className="text-xs font-medium"
                      style={{
                        color:
                          m.org_role === "owner"
                            ? "#a855f7"
                            : m.org_role === "admin"
                              ? "#3b82f6"
                              : "#374151",
                      }}
                    >
                      {m.org_role}
                    </span>
                  )}
                </td>
                <td className="py-2 pr-2 text-xs text-foreground-muted">
                  {new Date(m.joined_at).toISOString().slice(0, 10)}
                </td>
                <td className="py-2 pr-2 text-xs text-foreground-muted">
                  {m.last_login_at
                    ? new Date(m.last_login_at).toISOString().slice(0, 10)
                    : "—"}
                </td>
                {isAdmin && (
                  <td className="py-2 text-xs">
                    {m.account_id !== currentAccountId && (
                      <Button
                        variant="link"
                        size="sm"
                        className="text-danger"
                        onClick={() => {
                          if (
                            confirm(
                              `Remove ${m.display_name} from this org?`,
                            )
                          ) {
                            remove.mutate(m.account_id);
                          }
                        }}
                        disabled={remove.isPending}
                      >
                        Remove
                      </Button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function InvitesTab({
  orgId,
  canManage,
}: {
  orgId: string;
  canManage: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [showCreate, setShowCreate] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const invites = useQuery({
    queryKey: ["org-invites", orgId],
    queryFn: () => listOrgInvites(orgId, statusFilter === "all" ? undefined : statusFilter),
    enabled: statusFilter !== "all" || true,
  });
  const filteredInvites = invites.data?.invites ?? [];

  const revoke = useMutation({
    mutationFn: (inviteId: string) => revokeOrgInvite(orgId, inviteId),
    onSuccess: () => {
      toast.show({ kind: "success", title: "Invite revoked" });
      qc.invalidateQueries({ queryKey: ["org-invites", orgId] });
    },
    onError: (e) =>
      toast.show({
        kind: "error",
        title: "Revoke failed",
        description: (e as Error).message,
      }),
  });

  const resend = useMutation({
    mutationFn: (inviteId: string) => resendOrgInvite(orgId, inviteId),
    onSuccess: (data) => {
      toast.show({
        kind: "success",
        title: "Invite link refreshed",
        description: `Copied URL: ${data.invite_url.slice(0, 60)}…`,
      });
      navigator.clipboard?.writeText(data.invite_url).catch(() => {});
      qc.invalidateQueries({ queryKey: ["org-invites", orgId] });
    },
    onError: (e) =>
      toast.show({
        kind: "error",
        title: "Refresh failed",
        description: (e as Error).message,
      }),
  });

  return (
    <section className="border-t border-line-hairline pt-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-foreground-strong">Invites</h3>
        <div className="flex items-center gap-2">
          <Select
            items={{
              all: "All statuses",
              pending: "Pending",
              accepted: "Accepted",
              revoked: "Revoked",
              expired: "Expired",
            }}
            value={statusFilter}
            onValueChange={(v) => setStatusFilter(v as string)}
          >
            <Tooltip>
              <TooltipTrigger
                render={
                  <SelectTrigger className="text-xs py-0.5">
                    <SelectValue />
                    <SelectIcon />
                  </SelectTrigger>
                }
              />
              <TooltipContent>Filter by invite status</TooltipContent>
            </Tooltip>
            <SelectContent>
              <SelectList>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="accepted">Accepted</SelectItem>
              <SelectItem value="revoked">Revoked</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
            </SelectList>
            </SelectContent>
          </Select>
          {canManage && (
            <Button
              variant="primary"
              className="text-xs"
              onClick={() => setShowCreate(true)}
            >
              + Invite link
            </Button>
          )}
        </div>
      </div>
      {!canManage && (
        <p className="text-xs text-warning mb-2">
          ⚠ Owner / admin required to manage invites.
        </p>
      )}
      {invites.isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      )}
      {invites.error && (
        <p className="text-danger">Failed: {(invites.error as Error).message}</p>
      )}
      {invites.data && filteredInvites.length === 0 && (
        <EmptyState>
          <EmptyStateTitle>
            {statusFilter === "all"
              ? "No pending invites."
              : `No ${statusFilter} invites.`}
          </EmptyStateTitle>
        </EmptyState>
      )}
      {invites.data && filteredInvites.length > 0 && (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-foreground-muted text-left border-b border-line-hairline">
              <th className="font-normal py-1 pr-2">Email</th>
              <th className="font-normal py-1 pr-2">Role</th>
              <th className="font-normal py-1 pr-2">Status</th>
              <th className="font-normal py-1 pr-2">Expires</th>
              {canManage && <th className="font-normal py-1">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filteredInvites.map((inv) => (
              <tr
                key={inv.id}
                className="border-b border-line-hairline hover:bg-fill-muted"
              >
                <td className="py-2 pr-2 font-mono text-xs">{inv.email}</td>
                <td className="py-2 pr-2 text-xs">{inv.role}</td>
                <td className="py-2 pr-2 text-xs">
                  <InviteStatusBadge status={inv.status} />
                </td>
                <td className="py-2 pr-2 text-xs text-foreground-muted">
                  {new Date(inv.expires_at).toISOString().slice(0, 10)}
                </td>
                {canManage && inv.status === "pending" && (
                  <td className="py-2 text-xs space-x-2">
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => resend.mutate(inv.id)}
                      disabled={resend.isPending}
                    >
                      Refresh link
                    </Button>
                    <Button
                      variant="link"
                      size="sm"
                      className="text-danger"
                      onClick={() => {
                        if (confirm(`Revoke invite to ${inv.email}?`)) {
                          revoke.mutate(inv.id);
                        }
                      }}
                      disabled={revoke.isPending}
                    >
                      Revoke
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showCreate && (
        <CreateInviteDialog
          orgId={orgId}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            qc.invalidateQueries({ queryKey: ["org-invites", orgId] });
          }}
        />
      )}
    </section>
  );
}

function CreateInviteDialog({
  orgId,
  onClose,
  onCreated,
}: {
  orgId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"member" | "viewer">("member");
  const [message, setMessage] = useState("");
  const toast = useToast();
  const create = useMutation({
    mutationFn: () =>
      createOrgInvite(orgId, {
        email: email.trim().toLowerCase(),
        role,
        message: message.trim() ? message.trim() : null,
      }),
    onSuccess: (data) => {
      toast.show({
        kind: "success",
        title: `Invite link created for ${email}`,
        description: `Copied URL: ${data.invite_url.slice(0, 60)}…`,
      });
      // Copy invite URL to clipboard for the admin to share
      navigator.clipboard?.writeText(data.invite_url).catch(() => {});
      onCreated();
    },
    onError: (e) =>
      toast.show({
        kind: "error",
        title: "Invite failed",
        description: (e as Error).message,
      }),
  });
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create member invite link</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <form
            id="create-invite-form"
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate();
            }}
            className="space-y-3"
          >
            <div>
              <label className="block text-sm font-medium text-foreground-strong mb-1">Email</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-strong mb-1">Role</label>
              <Select
                items={{
                  member: "member (default)",
                  viewer: "viewer (read-only)",
                }}
                value={role}
                onValueChange={(v) => setRole(v as "member" | "viewer")}
              >
                <SelectTrigger>
                  <SelectValue />
                  <SelectIcon />
                </SelectTrigger>
                <SelectContent>
                  <SelectList>
                  <SelectItem value="member">member (default)</SelectItem>
                  <SelectItem value="viewer">viewer (read-only)</SelectItem>
                </SelectList>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-strong mb-1">Message (optional)</label>
              <Textarea
                className="text-xs min-h-[60px]"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>
          </form>
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="create-invite-form"
            variant="primary"
            disabled={create.isPending || !email.trim()}
          >
            {create.isPending ? "Creating…" : "Create invite link"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InviteStatusBadge({ status }: { status: string }) {
  const map: Record<string, BadgeVariant> = {
    pending: "information",
    accepted: "success",
    revoked: "muted",
    expired: "warning",
  };
  return <Badge variant={map[status] ?? "muted"}>{status}</Badge>;
}

function AuditTab({
  orgId,
  canView,
}: {
  orgId: string;
  canView: boolean;
}) {
  const audit = useQuery({
    queryKey: ["org-audit", orgId],
    queryFn: () => listOrgAuditLogs(orgId, 100),
    enabled: canView,
  });
  return (
    <section className="border-t border-line-hairline pt-5">
      <h3 className="text-sm font-medium text-foreground-strong mb-3">Audit log</h3>
      {!canView && (
        <p className="text-xs text-warning mb-2">
          ⚠ Org member required to view audit log.
        </p>
      )}
      {audit.isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
        </div>
      )}
      {audit.error && (
        <p className="text-danger">Failed: {(audit.error as Error).message}</p>
      )}
      {audit.data && audit.data.logs.length === 0 && (
        <EmptyState>
          <EmptyStateTitle>No audit log entries yet.</EmptyStateTitle>
        </EmptyState>
      )}
      {audit.data && audit.data.logs.length > 0 && (
        <div className="overflow-x-auto max-w-full">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-foreground-muted text-left border-b border-line-hairline">
              <th className="font-normal py-1 pr-2">When</th>
              <th className="font-normal py-1 pr-2">Actor</th>
              <th className="font-normal py-1 pr-2">App</th>
              <th className="font-normal py-1 pr-2">Action</th>
              <th className="font-normal py-1 pr-2">Payload</th>
            </tr>
          </thead>
          <tbody>
            {audit.data.logs.map((log) => {
              const actorName =
                log.actor_display_name ||
                (log.actor_username ? `@${log.actor_username}` : null) ||
                log.actor ||
                "—";
              return (
                <tr
                  key={log.id}
                  className="border-b border-line-hairline hover:bg-fill-muted"
                >
                  <td className="py-1 pr-2 text-foreground-muted font-mono whitespace-nowrap">
                    {new Date(log.created_at).toISOString().slice(0, 19)}Z
                  </td>
                  <td className="py-1 pr-2">
                    <span className="inline-flex items-center gap-1">
                      <Avatar
                        size="xs"
                        type={log.actor_type === "agent" ? "agent" : "human"}
                      >
                        {log.actor_avatar_url ? (
                          <AvatarImage src={log.actor_avatar_url} alt="" />
                        ) : null}
                        <AvatarFallback>
                          {actorName.slice(0, 1).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <span>{actorName}</span>
                      {log.actor_type === "agent" && (
                        <Badge variant="accent">agent</Badge>
                      )}
                      {log.actor_type === "system" && (
                        <Badge variant="muted">system</Badge>
                      )}
                    </span>
                  </td>
                  <td className="py-1 pr-2 font-mono text-foreground-muted whitespace-nowrap">
                    {log.app_slug ?? log.app_id?.slice(0, 8) ?? "—"}
                  </td>
                  <td className="py-1 pr-2 font-mono whitespace-nowrap">{log.action}</td>
                  <td className="py-1 pr-2 font-mono truncate max-w-md">
                    {log.payload}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      )}
    </section>
  );
}

function Row({
  k,
  v,
  mono,
  color,
}: {
  k: string;
  v: string;
  mono?: boolean | undefined;
  color?: string | undefined;
}) {
  return (
    <div className="flex items-center gap-2">
      <div className="w-48 text-xs text-foreground-muted">{k}</div>
      <div
        className={mono ? "font-mono text-xs" : "text-sm"}
        style={{ color }}
      >
        {v}
      </div>
    </div>
  );
}

// ============================================================================
// Webhooks tab (P2.5.8)
// ============================================================================

function WebhooksTab({
  orgId,
  canManage,
}: {
  orgId: string;
  canManage: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [showCreate, setShowCreate] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const webhooks = useQuery({
    queryKey: ["org-webhooks", orgId],
    queryFn: () => listOrgWebhooks(orgId),
    enabled: canManage,
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteOrgWebhook(orgId, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["org-webhooks", orgId] });
      toast.show({ kind: "success", title: "Webhook archived" });
    },
    onError: (e) =>
      toast.show({
        kind: "error",
        title: "Delete failed",
        description: (e as Error).message,
      }),
  });

  const toggle = useMutation({
    mutationFn: (input: { id: string; enabled: boolean }) =>
      updateOrgWebhook(orgId, input.id, { enabled: input.enabled }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["org-webhooks", orgId] }),
  });

  return (
    <div className="space-y-6">
      <section className="border-t border-line-hairline pt-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-medium text-foreground-strong">Webhooks</h3>
            <p className="text-xs text-foreground-muted">
              Subscribe external HTTP endpoints to release and build events.
              Deliveries are signed with HMAC SHA-256
              (<code className="font-mono">X-Hands-Signature</code>).
            </p>
          </div>
          {canManage && (
            <Button
              variant="primary"
              className="text-xs"
              onClick={() => setShowCreate(true)}
            >
              + Add webhook
            </Button>
          )}
        </div>

        {!canManage && (
          <p className="text-xs text-warning mb-2">
            ⚠ Org owner / admin required to manage webhooks.
          </p>
        )}

        {webhooks.isLoading && (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        )}
        {webhooks.error && (
          <p className="text-danger text-xs">
            Failed: {(webhooks.error as Error).message}
          </p>
        )}
        {webhooks.data && webhooks.data.webhooks.length === 0 && (
          <EmptyState>
            <EmptyStateTitle>No webhooks configured yet.</EmptyStateTitle>
          </EmptyState>
        )}

        {webhooks.data && webhooks.data.webhooks.length > 0 && (
          <div className="space-y-2">
            {webhooks.data.webhooks.map((w) => (
              <WebhookRow
                key={w.id}
                orgId={orgId}
                webhook={w}
                expanded={expandedId === w.id}
                onToggleExpand={() =>
                  setExpandedId((cur) => (cur === w.id ? null : w.id))
                }
                onToggleEnabled={(enabled) =>
                  toggle.mutate({ id: w.id, enabled })
                }
                onDelete={() => {
                  if (confirm(`Archive webhook ${w.url}?`)) remove.mutate(w.id);
                }}
                canManage={canManage}
              />
            ))}
          </div>
        )}
      </section>

      <section className="border-t border-line-hairline pt-5 text-xs text-foreground">
        <h4 className="text-sm font-medium text-foreground-strong mb-1">Delivery semantics</h4>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            Pending deliveries are retried every 5 minutes.
          </li>
          <li>
            Retries use exponential backoff: 5m → 30m → 2h (3 attempts max),
            then marked permanently <code className="font-mono">failed</code>.
          </li>
          <li>
            Receiver must verify
            <code className="font-mono"> X-Hands-Signature: sha256=&lt;hmac&gt;</code>
            using the webhook&apos;s secret (legacy{" "}
            <code className="font-mono">X-Quiver-Signature</code> is still sent too).
          </li>
        </ul>
      </section>

      {showCreate && (
        <CreateWebhookDialog
          orgId={orgId}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            qc.invalidateQueries({ queryKey: ["org-webhooks", orgId] });
          }}
        />
      )}
    </div>
  );
}

function WebhookRow({
  orgId,
  webhook,
  expanded,
  onToggleExpand,
  onToggleEnabled,
  onDelete,
  canManage,
}: {
  orgId: string;
  webhook: Webhook;
  expanded: boolean;
  onToggleExpand: () => void;
  onToggleEnabled: (enabled: boolean) => void;
  onDelete: () => void;
  canManage: boolean;
}) {
  let events: string[] = [];
  try {
    events = JSON.parse(webhook.events_json);
  } catch {
    events = [];
  }
  const deliveries = useQuery({
    queryKey: ["webhook-deliveries", orgId, webhook.id],
    queryFn: () => listWebhookDeliveries(orgId, webhook.id),
    enabled: expanded,
  });
  return (
    <div className="rounded-md bg-fill-muted p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Badge variant={webhook.enabled === 1 ? "success" : "muted"}>
              {webhook.enabled === 1 ? "enabled" : "disabled"}
            </Badge>
            <span className="font-mono text-xs truncate">{webhook.url}</span>
          </div>
          <div className="text-xs text-foreground-muted mt-1">
            events:{" "}
            {events.length === 0 ? (
              <span className="italic">(all)</span>
            ) : (
              events.map((e) => (
                <Badge key={e} variant="information" className="mr-1">
                  {e}
                </Badge>
              ))
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canManage && (
            <Button
              variant="outline"
              className="text-xs"
              onClick={() => onToggleEnabled(webhook.enabled !== 1)}
              disabled={!onToggleEnabled}
            >
              {webhook.enabled === 1 ? "Disable" : "Enable"}
            </Button>
          )}
          <Button
            variant="outline"
            className="text-xs"
            onClick={onToggleExpand}
          >
            {expanded ? "Hide" : "Deliveries"}
          </Button>
          {canManage && (
            <Button variant="outline" className="text-xs" onClick={onDelete}>
              Archive
            </Button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-line-hairline">
          {deliveries.isLoading && (
            <p className="text-xs text-foreground-muted">Loading deliveries…</p>
          )}
          {deliveries.error && (
            <p className="text-danger text-xs">
              Failed: {(deliveries.error as Error).message}
            </p>
          )}
          {deliveries.data && deliveries.data.deliveries.length === 0 && (
            <p className="text-xs text-foreground-muted">No deliveries yet.</p>
          )}
          {deliveries.data && deliveries.data.deliveries.length > 0 && (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-foreground-muted text-left border-b border-line-hairline">
                  <th className="font-normal py-1 pr-2">When</th>
                  <th className="font-normal py-1 pr-2">Event</th>
                  <th className="font-normal py-1 pr-2">Status</th>
                  <th className="font-normal py-1 pr-2">Attempts</th>
                  <th className="font-normal py-1 pr-2">HTTP</th>
                  <th className="font-normal py-1 pr-2">Next / error</th>
                </tr>
              </thead>
              <tbody>
                {deliveries.data.deliveries.map((d: WebhookDelivery) => (
                  <tr
                    key={d.id}
                    className="border-b border-line-hairline hover:bg-fill-muted"
                  >
                    <td className="py-1 pr-2 font-mono text-foreground-muted">
                      {new Date(d.created_at).toISOString().slice(0, 19)}Z
                    </td>
                    <td className="py-1 pr-2 font-mono">{d.event_type}</td>
                    <td className="py-1 pr-2">
                      <Badge
                        variant={
                          d.status === "succeeded"
                            ? "success"
                            : d.status === "failed"
                              ? "danger"
                              : "information"
                        }
                      >
                        {d.status}
                      </Badge>
                    </td>
                    <td className="py-1 pr-2 font-mono">
                      {d.attempts}/{d.max_attempts}
                    </td>
                    <td className="py-1 pr-2 font-mono">
                      {d.last_response_status ?? "—"}
                    </td>
                    <td className="py-1 pr-2 font-mono text-foreground-muted truncate max-w-xs">
                      {d.last_error
                        ? d.last_error
                        : d.next_attempt_at
                          ? `next ${new Date(d.next_attempt_at).toISOString().slice(11, 19)}Z`
                          : d.completed_at
                            ? `done ${new Date(d.completed_at).toISOString().slice(11, 19)}Z`
                            : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function CreateWebhookDialog({
  orgId,
  onClose,
  onCreated,
}: {
  orgId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const toast = useToast();
  const [url, setUrl] = useState("");
  const [secret, setSecret] = useState("");
  const [events, setEvents] = useState<WebhookEventType[]>([]);

  const create = useMutation({
    mutationFn: () =>
      createOrgWebhook(orgId, {
        url: url.trim(),
        secret: secret.trim(),
        events,
      }),
    onSuccess: () => {
      toast.show({ kind: "success", title: "Webhook created" });
      onCreated();
    },
    onError: (e) =>
      toast.show({
        kind: "error",
        title: "Create failed",
        description: (e as Error).message,
      }),
  });

  const toggleEvent = (ev: WebhookEventType) => {
    setEvents((cur) =>
      cur.includes(ev) ? cur.filter((e) => e !== ev) : [...cur, ev],
    );
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add webhook</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <form
            id="create-webhook-form"
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate();
            }}
            className="space-y-3"
          >
            <div>
              <label className="block text-sm font-medium text-foreground-strong mb-1">URL</label>
              <Input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com/hooks/quiver"
                required
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-strong mb-1">Secret (HMAC)</label>
              <Input
                type="text"
                className="font-mono text-xs"
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                placeholder="at-least-16-random-bytes"
                required
              />
              <p className="text-xs text-foreground-muted mt-1">
                Used to sign deliveries via{" "}
                <code className="font-mono">X-Hands-Signature</code>. Choose a
                strong secret; receivers must verify the signature.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-strong mb-1">Events (empty = all)</label>
              <div className="grid grid-cols-2 gap-1">
                {WEBHOOK_EVENT_TYPES.map((ev) => (
                  <label
                    key={ev}
                    className="flex items-center gap-2 text-xs cursor-pointer"
                  >
                    <Checkbox
                      checked={events.includes(ev)}
                      onCheckedChange={() => toggleEvent(ev)}
                    />
                    <span className="font-mono">{ev}</span>
                  </label>
                ))}
              </div>
            </div>
          </form>
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="create-webhook-form"
            variant="primary"
            disabled={
              create.isPending || !url.trim() || secret.trim().length < 8
            }
          >
            {create.isPending ? "Creating…" : "Create webhook"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
